import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CloseButton,
  Col,
  Collapse,
  Form,
  ListGroup,
  Modal,
  Stack,
} from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import { useNavigate } from "react-router-dom";
import { useAuth, postWithCredentials } from "../contexts/AuthContext";
import ComponentCard from "./new/ComponentCard";
import FeedbackAlert from "./new/FeedbackAlert";
import FileUploadBox from "./new/FileUploadBox";
import LoadingButton from "./LoadingButton";
import { PhoneNumberUtil } from "google-libphonenumber";
import axios from "axios";
import notifications from "../assets/json/notifications.json";
import JSZip from "jszip";
import { oldDownloadFile, oldEncrypt } from "../services/encryption";
import { sendEncryptionData } from "../services/server-connector";
import { ENCRYPTION_SUCCESS } from "../assets/js/constants";

const phoneUtil = PhoneNumberUtil.getInstance();

export function bytesToString(bytes) {
  const denominations = ["KB", "MB", "GB", "TB"];
  // 1 KB = 2^10 bytes.
  // 1 MB = 2^20 bytes.
  // 1 GB = 2^30 bytes, and so on.
  const bitPlaces = Math.floor(Math.log2(bytes));
  let index = Math.floor(bitPlaces / 10) - 1;
  if (index < 0) {
    index = 0;
  } else if (index > 3) {
    index = 3;
  }
  const largestUnitInBytes = Math.pow(2, (index + 1) * 10);

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
  const formattedNumber = formatter.format(bytes / largestUnitInBytes);
  return `${formattedNumber} ${denominations[index]}`;
}

const today = new Date();
const defaultExpiryDate = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() + 1
);
const minEncryptExpiry = defaultExpiryDate.toISOString().slice(0, 16);
const maxEncryptExpiry = new Date(
  today.getFullYear() + 1,
  today.getMonth(),
  today.getDate()
)
  .toISOString()
  .slice(0, 16);
const defaultEncryptionOptions = {
  expiryDateTime: minEncryptExpiry,
  decryptionLimit: 1,
  fileName: `New File (Expires ${defaultExpiryDate.toString().slice(0, 21)})`,
  myEmail: "",
  receiver2FA: "None",
};

function InlineFormInput({ label, children, className, ...rest }) {
  const controlId = `form-${label.split("s+").join("")}`;
  return (
    <Form.Group
      controlId={controlId}
      className={`row gx-3 ${className}`}
      {...rest}
    >
      <Col xs={12} sm={"auto"}>
        <Form.Label column style={{ width: "14ch" }}>
          {label}
        </Form.Label>
      </Col>
      <Col>{children}</Col>
    </Form.Group>
  );
}

function InlineFormSwitch({ label, value, onChange, ...rest }) {
  return (
    <Alert
      className={`d-flex py-1 ps-3 ${value ? "" : "bg-light"}`}
      variant={value ? "primary" : "secondary"}
      style={{
        border: "none",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      {...rest}
    >
      <Form.Label column style={{ userSelect: "none" }}>
        {label}
      </Form.Label>
      <Form.Check type="switch" checked={value} onChange={onChange} />
    </Alert>
  );
}

export default function Encrypt(props) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  // invariant: user is signed in
  const userEmail = currentUser.email;

  // states
  const [files, setFiles] = useState([]);
  const [encryptionOptions, setEncryptionOptions] = useState({
    ...defaultEncryptionOptions,
    myEmail: currentUser && currentUser.email,
  });
  const [isCustomExpiry, setIsCustomExpiry] = useState(false);
  const [isNotifyUserOnDecrypt, setIsNotifyUserOnDecrypt] = useState(false);
  // medical specific states:
  const [recipient, setRecipient] = useState("");
  const [isSendDecryptMessage, setIsSendDecryptMessage] = useState(false);
  const [decryptMessage, setDecryptMessage] = useState("");

  const [feedback, setFeedback] = useState(null);
  const [serviceUseable, setServiceUseable] = useState(false);
  const [serviceNotification, setServiceNotification] = useState(null);
  const [userOutOfUses, setUserOutOfUses] = useState(false);
  
  const handleEncryptionOptionsChange = (e) => {
    e.preventDefault();
    setEncryptionOptions({
      ...encryptionOptions,
      [e.target.name]: e.target.value,
    });
  };

  useEffect(() => {
    // reset custom expiry if disabled
    if (!isCustomExpiry) {
      setEncryptionOptions((opt) => ({
        ...opt,
        expiryDateTime: minEncryptExpiry,
      }));
    }
  }, [isCustomExpiry]);

  useEffect(() => {
    // reset custom email notification if disabled
    if (!isNotifyUserOnDecrypt) {
      setEncryptionOptions((opt) => ({
        ...opt,
        myEmail: currentUser && currentUser.email,
      }));
    }
  }, [isNotifyUserOnDecrypt, currentUser]);

  useEffect(() => {
    // reset custom message on decrypt if disabled
    if (!isSendDecryptMessage) {
      setDecryptMessage("");
    }
  }, [isSendDecryptMessage]);

  useEffect(() => {
    // check service useable
    if (currentUser) {
      checkService(currentUser.email);
    }
  }, [currentUser]);

  useEffect(() => {
    setEncryptionOptions((opt) => {
      if (opt.fileName === "" || opt.fileName.startsWith("New File (Expires")) {
        return {
          ...opt,
          fileName:
            "New File (Expires " +
            new Date(opt.expiryDateTime).toString().slice(0, 21) +
            ")",
        };
      } else {
        return opt;
      }
    });
  }, [encryptionOptions.expiryDateTime]);

  async function checkService(email) {
    setFeedback({
      variant: "info",
      message: "Getting Subscription Information",
      loading: true,
    });
    // check service useable
    try {
      // console.log("getting sub info");
      const res = await postWithCredentials(
        `${process.env.REACT_APP_SERVER_URL}checkServiceUsable`,
        {
          userEmail: email,
          service: "files_encrypted",
        }
      );
      setServiceUseable(res.data.usable);
      if (!res.data.usable) {
        // not useable, notify and disabled
        if (res.data.fpp === 2) {
          setServiceNotification(notifications.userData.employeeOutOfUses);
        } else if (res.data.fpp === 1) {
          setServiceNotification(notifications.userData.outOfUses);
          setUserOutOfUses(true);
        } else {
          setServiceNotification(notifications.userData.subscriptionNotFound);
        }
      }
      setFeedback(null);

      // console.log("usable", res.data.usable);
      return res.data.usable;
    } catch (e) {
      console.log("err while checking service", e);
      return false;
    }
  }

  // input validation of "soft" rules. Hard rules (like date input, email
  // input, and non-empty checks) are enforced by browser.
  function validateInputs() {
    const validation = [];

    // validate file length
    if (files.length <= 0) {
      validation.push("Please upload a file to encrypt.");
    }

    // validate date
    if (
      Date(encryptionOptions["expiryDateTime"]) < Date(minEncryptExpiry) &&
      Date(encryptionOptions["expiryDateTime"]) > Date(maxEncryptExpiry)
    ) {
      validation.push(`Date must be after ${minEncryptExpiry}`);
    }

    // validate sms fields
    try {
      const phoneNum = phoneUtil.parseAndKeepRawInput("+" + recipient);
      if (!phoneUtil.isValidNumber(phoneNum)) {
        validation.push(`Recipient must have a valid phone number`);
      }
    } catch (e) {
      validation.push(`Recipient must have a valid phone number`);
    }

    if (validation.length > 0) {
      setFeedback({
        variant: "warning",
        message: validation.map((str, i) => <span key={i}>{str}</span>),
      });
      return false;
    }

    return true;
  }

  async function encryptFile() {
    // invariant: data already validated && user has service
    setFeedback({
      loading: true,
      message: "Encrypting file...",
      variant: "info",
    });

    // create zip file of files
    let zip = new JSZip();
    // add all the files to the zip
    files.forEach((file) => {
      zip.file(file.name, file);
    });

    try {
      const blob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
      });
      const zipFile = new File([blob], "zipped.zip", {
        type: "blob",
        compression: "DEFLATE",
      });

      // call encrypt/oldEncrypt
      const [x2Hashed, xOrKey, initHashed, iv, otp, encryptedFileData] =
        await oldEncrypt(zipFile);

      const sendResult = await sendEncryptionData({
        init: x2Hashed,
        key: xOrKey,
        proof: initHashed,
        iv: iv,
        otp: otp,
        uses: encryptionOptions["decryptionLimit"],
        time: new Date(encryptionOptions["expiryDateTime"]),
        email: encryptionOptions["myEmail"],
        recipientContact: recipient,
        filename: encryptionOptions["fileName"],
        message: decryptMessage,
        smsSentTime: 0,
        isNotifEnabled: isNotifyUserOnDecrypt,
      });

      if (sendResult.message === ENCRYPTION_SUCCESS) {
        // save file to computer
        oldDownloadFile(
          encryptedFileData.ciphertext,
          `${encryptionOptions.fileName}.cy`
        );
        setFeedback({
          variant: "success",
          message: "File Successfully Encrypted!",
        });
      } else {
        setFeedback({
          variant: "danger",
          message: "A problem occurred while encrypting your file.",
        });
      }
      return true;
    } catch (e) {
      setFeedback({
        variant: "danger",
        message: "A problem occurred while encrypting your file.",
      });
      return false;
    }
  }

  async function onSubmit(e) {
    e.preventDefault();
    setFeedback({
      loading: true,
      variant: "info",
      message: "Submitting...",
    });

    // validation check
    if (!validateInputs()) return;

    try {
      // service check
      const isValidService = await checkService(currentUser.email);
      if (!isValidService) return;

      // encrypt file
      const fileSuccessfullyEncrypted = await encryptFile();
      if (!fileSuccessfullyEncrypted) return;
    } catch (e) {
      console.log(e);
    }

    // update service count
    const payload1 = {
      email: currentUser.email,
      service: "files_encrypted",
      id: currentUser.id,
      count: 1,
    };
    postWithCredentials(
        process.env.REACT_APP_PAYMENT_SYSTEM_SERVER_URL + "updateServiceCount",
        payload1
      )
      .then(() => {})
      .catch((e) => {
        console.error("Update service count failed", e);
      })
      .finally(() => {
        // clear all options
        setEncryptionOptions({
          ...defaultEncryptionOptions,
          myEmail: currentUser.email,
        });
        setIsCustomExpiry(false);
        setIsNotifyUserOnDecrypt(false);
        setIsSendDecryptMessage(false);
        setDecryptMessage("");
        setRecipient("");
        setFiles([]);
      });
  }

  const helpContent = (
    <p className="mb-1">
      Start by clicking on <b>Click to add or drop files here</b>. There are a
      few parameters that you can change before encrypting the file:
      <ul>
        <li>
          <b>Expiry Date/Time</b>: You can customize when the encrypted file
          will expire. Afterwards, it cannot be decrypted.
        </li>
        <li>
          <b>File Name</b>: A name for the encrypted file.
        </li>
        <li>
          <b>Notify Me When Recipient Decrypts File</b>: You can choose to
          recieve an email whenever this file is decrypted.
        </li>
        <li>
          <b>Send Custom SMS to Recipient</b>: Allows the sender to compose a
          message tailored to the particular case/client/document that will be
          included in the SMS sent to the recipient cell phone.
        </li>
        <li>
          <b>Recipient Phone Number</b>: Recipient will be authenticated by
          receiving an SMS. Fill in the recipient’s cell phone number. Make sure
          that the recipient can receive SMS.
        </li>
      </ul>
      Once ready, press <b>Submit</b> to encrypt the file. The resultant file
      will be downloaded to your computer for you to share by your method of
      choice.
    </p>
  );

  return (
    <ComponentCard title="Encrypt Files" helpContent={helpContent}>
      {/* FILE UPLOAD CARD */}
      <Card
        style={{
          boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.25)",
        }}
      >
        <FileUploadBox
          handleFileUpload={(uploads) => {
            // uploads is a FileList, iterate and add to files
            const uploadArray = [];
            for (const file of uploads) {
              uploadArray.push(file);
            }
            setFiles(files.concat(uploadArray));
          }}
          style={{
            minHeight: 120,
            height: files.length > 0 ? 120 : "100%",
            display: "flex",
            borderRadius: 10,
            margin: 10,
            border: "2px dashed gray",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Card.Text>Click to add or drop files here...</Card.Text>
        </FileUploadBox>
        {/* List of uploaded files*/}
        <Card.Body
          className="pt-2"
          style={{
            display: files.length > 0 ? null : "none",
          }}
        >
          <span
            className="ms-3 my-1"
            style={{
              color: "#424242",
            }}
          >
            <b>Files Uploaded</b>
          </span>
          <ListGroup
            style={{
              maxHeight: "20vh",
              overflow: "scroll",
              width: "100%",
            }}
          >
            {files.map((file, index) => {
              return (
                <ListGroup.Item key={index}>
                  {`${file.name} - ${bytesToString(file.size)}`}
                  <CloseButton
                    style={{ float: "right" }}
                    aria-label="Remove Upload"
                    onClick={(e) => {
                      setFiles(files.filter((_, i) => i !== index));
                    }}
                  />
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </Card.Body>
      </Card>
      {/* ENCRYPTION OPTION FORM */}
      <Form className="mt-3 px-2" onSubmit={onSubmit}>
        <InlineFormInput label={"File Name"} className="mb-2">
          <Form.Control
            name="fileName"
            value={encryptionOptions["fileName"]}
            onChange={handleEncryptionOptionsChange}
            required
          />
        </InlineFormInput>
        <hr className="my-3" />
        <Form.Group controlId="CustomExpirySwitch" className="mb-1">
          <InlineFormSwitch
            label="Set a Custom Expiry Date/Time"
            value={isCustomExpiry}
            onChange={(e) => {
              setIsCustomExpiry(e.target.checked);
            }}
          />
        </Form.Group>
        <Collapse in={isCustomExpiry}>
          <div>
            <InlineFormInput label={"Expiry Date/Time"} className="pb-3">
              <Form.Control
                type="datetime-local"
                disabled={!isCustomExpiry}
                name="expiryDateTime"
                min={minEncryptExpiry}
                max={maxEncryptExpiry}
                value={encryptionOptions["expiryDateTime"]}
                onChange={handleEncryptionOptionsChange}
                required
              />
            </InlineFormInput>
          </div>
        </Collapse>
        <Form.Group controlId="NotifyMe" className="mb-1">
          <InlineFormSwitch
            label="Notify Me When Recipient Decrypts File"
            value={isNotifyUserOnDecrypt}
            onChange={(e) => {
              setEncryptionOptions({
                ...encryptionOptions,
                myEmail: userEmail,
              });
              setIsNotifyUserOnDecrypt(e.target.checked);
            }}
          />
        </Form.Group>
        <Collapse in={isNotifyUserOnDecrypt}>
          <div>
            <InlineFormInput label={"My Email"} className="pb-3">
              <Form.Control
                type="email"
                disabled={!isNotifyUserOnDecrypt}
                name="myEmail"
                value={encryptionOptions["myEmail"]}
                onChange={handleEncryptionOptionsChange}
                required
              />
            </InlineFormInput>
          </div>
        </Collapse>
        <hr className="mb-3 mt-0" />
        <InlineFormInput label={"Recipient Phone"} className="pb-3">
          <PhoneInput
            className="phone"
            country="ca"
            style={{ width: "100%" }}
            inputProps={{ style: { width: "100%" }, required: true }}
            value={recipient}
            onChange={(number) => {
              setRecipient(number);
            }}
          />
        </InlineFormInput>
        <Form.Group controlId="CustomDecryptionMessageSwitch">
          <InlineFormSwitch
            label="Send Custom SMS To Recipient"
            value={isSendDecryptMessage}
            onChange={(e) => {
              setIsSendDecryptMessage(e.target.checked);
            }}
          />
        </Form.Group>
        <Collapse in={isSendDecryptMessage}>
          <div>
            <InlineFormInput label="Message" className="pb-3">
              <Form.Control
                as="textarea"
                disabled={!isSendDecryptMessage}
                value={decryptMessage}
                onChange={(e) => {
                  setDecryptMessage(e.target.value);
                }}
              />
            </InlineFormInput>
          </div>
        </Collapse>
        <LoadingButton
          type="submit"
          className="w-100"
          loading={feedback && feedback.loading}
          disabled={files.length <= 0 || !serviceUseable}
        >
          Submit
        </LoadingButton>
      </Form>
      <FeedbackAlert feedback={feedback} className="mt-2 mb-0" />
      <hr className="my-3" />
      <span>
        <strong>Note</strong>: Remember, your file can only be decrypted once.
      </span>

      <Modal show={serviceNotification} backdrop={"static"}>
        <Modal.Header>
          <Modal.Title>
            <b>{serviceNotification && serviceNotification.title}</b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {serviceNotification && serviceNotification.message}
          <Stack direction="horizontal" gap={2} className="mt-3">
            <Button
              className="w-100"
              variant="secondary"
              onClick={() => {
                navigate("/dashboard");
              }}
            >
              Dashboard
            </Button>
            {userOutOfUses &&
              <Button
                className="w-100"
                onClick={() => {
                  navigate("/chunk");
                }}
              >
                Buy Chunks
              </Button>
            }
            <Button
              className="w-100"
              onClick={() => {
                navigate("/upgrade-service");
              }}
            >
              Upgrade Service
            </Button>
          </Stack>
        </Modal.Body>
      </Modal>
    </ComponentCard>
  );
}
